import { useState, useEffect, Fragment } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  Table,
  Text,
  Group,
  Button,
  TextInput,
  Stack,
  Switch,
  Textarea,
  Modal,
  Alert,
  ActionIcon,
  Menu,
  Tooltip,
  Pagination,
  Divider,
  ScrollArea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  IconPlus,
  IconAlertCircle,
  IconDots,
  IconPencil,
  IconTrash,
  IconHistory,
  IconEye,
  IconEyeOff,
  IconRocket,
  IconFileImport,
  IconCopy,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { variablesApi, deploymentsApi, servicesApi } from "../lib/api";
import type { EnvironmentVariable } from "../types";
import { PageHeader } from "../components/PageHeader";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { PageTransition } from "../components/PageTransition";
import { useAuth } from "../contexts/AuthContext";
import { ConfirmationModal } from "../components/ConfirmationModal";
import { showSuccess, showError } from "../lib/notifications";

interface CreateVariableForm {
  key: string;
  value: string;
  isSecret: boolean;
  isBuildTime: boolean;
  description: string;
}

export function EnvironmentPage() {
  const { serviceId, envId } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedVariable, setSelectedVariable] =
    useState<EnvironmentVariable | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [envText, setEnvText] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [totalToImport, setTotalToImport] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [error, setError] = useState("");
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [redeployConfirmOpen, setRedeployConfirmOpen] = useState(false);
  const [doRebuild, setDoRebuild] = useState(false);

  // Get service data to check environment access
  const { data: service } = useQuery({
    queryKey: ["service", serviceId],
    queryFn: async () => {
      const response = await servicesApi.get(serviceId!);
      return response.data;
    },
    enabled: !!serviceId,
  });

  // Get current environment data
  const { data: environment } = useQuery({
    queryKey: ["environment", serviceId, envId],
    queryFn: async () => {
      const response = await servicesApi.get(serviceId!);
      const env = response.data.environments?.find((e: any) => e.id === envId);
      return env;
    },
    enabled: !!serviceId && !!envId,
  });

  // Check if environment is disabled
  const isEnvironmentDisabled = () => {
    return environment?.disabled === true;
  };

  const canManageVariables =
    ["SUPERUSER", "ADMIN", "DEVOPS"].includes(user?.role || "") &&
    !isEnvironmentDisabled();

  // Check if user can trigger deployments (admin or has write access to this environment)
  const canTriggerDeployment = () => {
    if (["SUPERUSER", "ADMIN"].includes(user?.role || "")) {
      return true;
    }

    if (service && envId) {
      const environment = service.environments?.find((env) => env.id === envId);
      return environment?.hasAccess && environment?.accessType === "WRITE";
    }

    return false;
  };

  const form = useForm<CreateVariableForm>({
    initialValues: {
      key: "",
      value: "",
      isSecret: false,
      isBuildTime: false,
      description: "",
    },
    validate: {
      key: (value) => {
        if (!value) return "Key is required";
        if (!/^[A-Z][A-Z0-9_]*$/.test(value))
          return "Key must start with uppercase letter and contain only uppercase letters, numbers, and underscores";
        return null;
      },
      value: (value) => (!value ? "Value is required" : null),
    },
  });

  const { data: variables, isLoading } = useQuery({
    queryKey: ["variables", serviceId, envId],
    queryFn: async () => {
      const response = await variablesApi.list(serviceId!, envId!);
      return response.data;
    },
    enabled: !!serviceId && !!envId && !isEnvironmentDisabled(),
  });

  const parseDotEnv = (
    content: string,
  ): Array<{ key: string; value: string }> => {
    const lines = content.split(/\r?\n/);
    const out: Array<{ key: string; value: string }> = [];
    for (const raw of lines) {
      const line = raw.trim();
      if (!line || line.startsWith("#")) continue;
      const eq = line.indexOf("=");
      if (eq <= 0) continue;
      const key = line.slice(0, eq).trim();
      let value = line.slice(eq + 1).trim();
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }
      if (!key) continue;
      out.push({ key, value });
    }
    return out;
  };

  const handleImportEnv = async () => {
    if (!serviceId || !envId) return;
    const pairs = parseDotEnv(envText);
    if (pairs.length === 0) return;
    setIsImporting(true);
    setTotalToImport(pairs.length);
    setImportedCount(0);
    setFailedCount(0);
    try {
      const promises = pairs.map((p) =>
        variablesApi
          .create(serviceId, envId, {
            key: p.key,
            value: p.value,
            isSecret: false,
            isBuildTime: false,
            description: "",
          })
          .then(() => setImportedCount((c) => c + 1))
          .catch(() => setFailedCount((c) => c + 1)),
      );

      const results = await Promise.allSettled(promises);
      const succeeded = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - succeeded;
      setImportedCount(succeeded);
      setFailedCount(failed);
      setImportModalOpen(false);
      setEnvText("");
      await queryClient.invalidateQueries(["variables", serviceId, envId]);
      showSuccess(`${succeeded} variable(s) imported`);
    } finally {
      setIsImporting(false);
    }
  };

  // Filter variables based on search and filter
  const handleExportEnv = async () => {
    const vars = variables || [];
    if (vars.length === 0) {
      showError("No variables to export");
      return;
    }
    const quote = (v: string) =>
      /[\s#"'=]/.test(v) || v === ""
        ? '"' + v.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n") + '"'
        : v;
    const content = [...vars]
      .sort((a: any, b: any) => a.key.localeCompare(b.key))
      .map((v: any) => `${v.key}=${quote(v.value ?? "")}`)
      .join("\n");
    try {
      await navigator.clipboard.writeText(content);
      showSuccess(`Copied ${vars.length} variable(s) to clipboard`);
    } catch {
      showError("Failed to copy to clipboard");
    }
  };

  const filteredVariables = variables?.filter(
    (variable: EnvironmentVariable) => {
      const matchesSearch = search
        ? variable.key.toLowerCase().includes(search.toLowerCase()) ||
          (!variable.isSecret &&
            variable.value.toLowerCase().includes(search.toLowerCase())) ||
          variable.description?.toLowerCase().includes(search.toLowerCase())
        : true;

      const matchesFilter = filter
        ? filter === "secret"
          ? variable.isSecret
          : !variable.isSecret
        : true;

      return matchesSearch && matchesFilter;
    },
  );

  // Calculate paginated variables
  const totalPages = Math.ceil((filteredVariables?.length || 0) / limit);
  const paginatedVariables = filteredVariables?.slice(
    (page - 1) * limit,
    page * limit,
  );

  // Reset page when search or filter changes
  useEffect(() => {
    setPage(1);
  }, [search, filter]);

  const createVariable = useMutation({
    mutationFn: async (data: CreateVariableForm) => {
      const response = await variablesApi.create(serviceId!, envId!, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["variables", serviceId, envId]);
      setCreateModalOpen(false);
      form.reset();
      setError("");
      showSuccess("Variable created successfully");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.details?.validationErrors
        ? error.response.data.details.validationErrors
            .map((err: any) => `${err.field}: ${err.message}`)
            .join("\n")
        : error.response?.data?.error || "Failed to create variable";
      setError(errorMessage);
      showError(error, "Failed to create variable");
    },
  });

  const updateVariable = useMutation({
    mutationFn: async (data: Omit<CreateVariableForm, "key">) => {
      if (!selectedVariable) return;
      const response = await variablesApi.update(
        serviceId!,
        envId!,
        selectedVariable.id,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["variables", serviceId, envId]);
      setEditModalOpen(false);
      form.reset();
      setError("");
      showSuccess("Variable updated successfully");
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.details?.validationErrors
        ? error.response.data.details.validationErrors
            .map((err: any) => `${err.field}: ${err.message}`)
            .join("\n")
        : error.response?.data?.error || "Failed to update variable";
      setError(errorMessage);
      showError(error, "Failed to update variable");
    },
  });

  const deleteVariable = useMutation({
    mutationFn: async () => {
      if (!selectedVariable) return;
      await variablesApi.delete(serviceId!, envId!, selectedVariable.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["variables", serviceId, envId]);
      setDeleteModalOpen(false);
      showSuccess("Variable deleted successfully");
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to delete variable");
    },
  });

  const triggerDeployment = useMutation({
    mutationFn: async () => {
      await deploymentsApi.triggerDeployment(serviceId!, envId!, {
        fullBuild: doRebuild,
      });
    },
    onSuccess: () => {
      showSuccess("Service redeployment triggered successfully");
      setRedeployConfirmOpen(false);
      setDoRebuild(false);
    },
    onError: (error: any) => {
      setError(error.response?.data?.error || "Failed to trigger redeployment");
    },
  });

  const { data: variableHistory } = useQuery({
    queryKey: ["variableHistory", serviceId, envId, selectedVariable?.id],
    queryFn: async () => {
      const response = await variablesApi.getHistory(
        serviceId!,
        envId!,
        selectedVariable!.id,
      );
      return response.data;
    },
    enabled: !!serviceId && !!envId && !!selectedVariable && historyModalOpen,
  });

  const handleCreateVariable = (values: CreateVariableForm) => {
    createVariable.mutate(values);
  };

  const handleUpdateVariable = (values: Omit<CreateVariableForm, "key">) => {
    updateVariable.mutate(values);
  };

  const handleDeleteVariable = () => {
    deleteVariable.mutate();
  };

  const handleRedeployService = () => {
    setRedeployConfirmOpen(true);
  };

  const confirmRedeployService = () => {
    triggerDeployment.mutate();
  };

  const toggleSecret = (id: string) => {
    setShowSecrets((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading && !isEnvironmentDisabled()) {
    return <LoadingOverlay message="Loading..." />;
  }

  return (
    <Fragment>
      <PageTransition>
        <PageHeader
          title="Environment Variables"
          description="Manage environment variables for your service"
          breadcrumbs={[
            { label: 'Services', to: '/services' },
            ...(service ? [{ label: service.name, to: `/services/${serviceId}` }] : []),
            { label: environment?.name || 'Environment' },
          ]}
        />

        {isEnvironmentDisabled() ? (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            mb="md"
            title="Environment Disabled - Access Restricted"
          >
            This environment is currently disabled. Variable access is
            completely restricted and not available for disabled environments.
            Deployments will be skipped, existing deployments will be deleted,
            and Cloud Run services will be removed when deployment is attempted.
          </Alert>
        ) : (
          <>
            {/* Import .env Modal */}
            <Modal
              opened={importModalOpen}
              onClose={() => !isImporting && setImportModalOpen(false)}
              title="Import variables from .env"
            >
              <Stack>
                <Text size="sm" color="dimmed">
                  Paste your .env content below. Lines like KEY=VALUE are
                  supported. Comments (#) and blank lines are ignored.
                </Text>
                <Textarea
                  minRows={10}
                  value={envText}
                  onChange={(e) => setEnvText(e.currentTarget.value)}
                  placeholder={"FOO=bar\nBAZ=qux"}
                  disabled={isImporting}
                />
                {isImporting && (
                  <Stack spacing={4}>
                    <Text size="sm">
                      Importing {importedCount + failedCount}/{totalToImport}...
                    </Text>
                    <Divider my="xs" />
                    <div>
                      {/* simple progress bar */}
                      <div
                        style={{
                          height: 6,
                          background: "#e9ecef",
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${
                              totalToImport
                                ? ((importedCount + failedCount) /
                                    totalToImport) *
                                  100
                                : 0
                            }%`,
                            height: "100%",
                            background: "#4dabf7",
                          }}
                        />
                      </div>
                      <Group position="apart" mt={6}>
                        <Text size="xs" color="dimmed">
                          Succeeded: {importedCount}
                        </Text>
                        <Text size="xs" color="dimmed">
                          Failed: {failedCount}
                        </Text>
                      </Group>
                    </div>
                  </Stack>
                )}
                <Divider my="xs" />
                <Group position="apart">
                  <Text size="sm" color="dimmed">
                    Parsed: {parseDotEnv(envText).length} variables
                  </Text>
                  <Group>
                    <Button
                      variant="default"
                      onClick={() => setImportModalOpen(false)}
                      disabled={isImporting}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleImportEnv}
                      loading={isImporting}
                      disabled={
                        parseDotEnv(envText).length === 0 || isImporting
                      }
                    >
                      Import
                    </Button>
                  </Group>
                </Group>
              </Stack>
            </Modal>

            <Card mt="md" shadow="sm" radius="md" withBorder>
              <Group
                p="md"
                position="apart"
                align="center"
                sx={(theme) => ({
                  [theme.fn.smallerThan('sm')]: {
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  },
                })}
              >
                <Group
                  spacing="md"
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    },
                  })}
                >
                  <TextInput
                    placeholder="Search variables..."
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                    sx={(theme) => ({
                      width: 300,
                      [theme.fn.smallerThan('sm')]: { width: '100%' },
                    })}
                  />
                  <TextInput
                    placeholder="Filter by type..."
                    value={filter}
                    onChange={(e) => setFilter(e.currentTarget.value)}
                    sx={(theme) => ({
                      width: 200,
                      [theme.fn.smallerThan('sm')]: { width: '100%' },
                    })}
                  />
                </Group>
                <Group spacing="sm" sx={{ flexWrap: 'wrap' }}>
                  {canManageVariables && (
                    <>
                      <Button
                        variant="outline"
                        leftIcon={<IconPlus size={16} />}
                        onClick={() => setCreateModalOpen(true)}
                      >
                        Add Variable
                      </Button>
                      <Button
                        variant="outline"
                        leftIcon={<IconFileImport size={16} />}
                        onClick={() => setImportModalOpen(true)}
                      >
                        Import .env
                      </Button>
                    </>
                  )}
                  {["SUPERUSER", "ADMIN"].includes(user?.role || "") && (
                    <Button
                      variant="outline"
                      leftIcon={<IconCopy size={16} />}
                      onClick={handleExportEnv}
                    >
                      Export .env
                    </Button>
                  )}
                  {canTriggerDeployment() && (
                    <Button
                      variant="filled"
                      leftIcon={<IconRocket size={16} />}
                      onClick={handleRedeployService}
                      loading={triggerDeployment.isLoading}
                      color={isEnvironmentDisabled() ? "orange" : "thanos"}
                      title={
                        isEnvironmentDisabled()
                          ? "Environment is disabled - Redeployment will be cancelled"
                          : "This will trigger redeployment of the service"
                      }
                    >
                      {isEnvironmentDisabled()
                        ? "Redeploy (Will Cancel)"
                        : "Redeploy Service"}
                    </Button>
                  )}
                </Group>
              </Group>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <ScrollArea>
                <Table>
                  <thead>
                    <tr>
                      <th style={{ maxWidth: "300px", width: "300px" }}>Key</th>
                      <th style={{ maxWidth: "400px", width: "400px" }}>
                        Value
                      </th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Last Updated</th>
                      <th style={{ width: "120px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {paginatedVariables?.map(
                        (variable: EnvironmentVariable, index: number) => (
                          <motion.tr
                            key={variable.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => {
                              if (canManageVariables) {
                                setSelectedVariable(variable);
                                form.setValues({
                                  key: variable.key,
                                  value: variable.value,
                                  isSecret: variable.isSecret,
                                  isBuildTime: variable.isBuildTime,
                                  description: variable.description || "",
                                });
                                setEditModalOpen(true);
                              }
                            }}
                            style={{
                              cursor: canManageVariables
                                ? "pointer"
                                : "default",
                              transition: "background-color 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (canManageVariables) {
                                e.currentTarget.style.backgroundColor =
                                  "rgba(255, 54, 33, 0.06)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                            }}
                          >
                            <td style={{ maxWidth: "300px", width: "300px" }}>
                              <Group spacing="xs" noWrap>
                                <Tooltip label={variable.key}>
                                  <Text weight={500} truncate>
                                    {variable.key}
                                  </Text>
                                </Tooltip>
                              </Group>
                            </td>
                            <td style={{ maxWidth: "400px", width: "400px" }}>
                              {variable.isSecret ? (
                                <Group spacing="xs" noWrap>
                                  <Tooltip
                                    label={
                                      showSecrets[variable.id]
                                        ? variable.value
                                        : "Click eye icon to reveal"
                                    }
                                    multiline
                                  >
                                    <Text truncate>
                                      {showSecrets[variable.id]
                                        ? variable.value
                                        : "••••••••••••"}
                                    </Text>
                                  </Tooltip>
                                  <ActionIcon
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSecret(variable.id);
                                    }}
                                  >
                                    {showSecrets[variable.id] ? (
                                      <IconEyeOff size={14} />
                                    ) : (
                                      <IconEye size={14} />
                                    )}
                                  </ActionIcon>
                                </Group>
                              ) : (
                                <Tooltip label={variable.value} multiline>
                                  <Text truncate>{variable.value}</Text>
                                </Tooltip>
                              )}
                            </td>
                            <td>
                              <Text size="sm" color="dimmed">
                                {variable.description || "No description"}
                              </Text>
                            </td>
                            <td>
                              <Group spacing="xs">
                                {variable.isSecret && (
                                  <Tooltip label="Secret variable">
                                    <Text size="xs" color="red" weight={500}>
                                      SECRET
                                    </Text>
                                  </Tooltip>
                                )}
                                {variable.isBuildTime && (
                                  <Tooltip label="Available at build time">
                                    <Text size="xs" color="teal" weight={500}>
                                      BUILD
                                    </Text>
                                  </Tooltip>
                                )}
                                {!variable.isSecret &&
                                  !variable.isBuildTime && (
                                    <Text size="xs" color="dimmed">
                                      NORMAL
                                    </Text>
                                  )}
                              </Group>
                            </td>
                            <td>
                              <Text size="sm" color="dimmed">
                                {new Date(variable.updatedAt).toLocaleString()}
                              </Text>
                            </td>
                            <td>
                              <Group spacing="xs">
                                <Tooltip label="View History">
                                  <ActionIcon
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedVariable(variable);
                                      setHistoryModalOpen(true);
                                    }}
                                  >
                                    <IconHistory size={16} />
                                  </ActionIcon>
                                </Tooltip>
                                {canManageVariables && (
                                  <Menu position="bottom-end">
                                    <Menu.Target>
                                      <ActionIcon
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <IconDots size={16} />
                                      </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                      <Menu.Item
                                        icon={<IconPencil size={14} />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedVariable(variable);
                                          form.setValues({
                                            key: variable.key,
                                            value: variable.value,
                                            isSecret: variable.isSecret,
                                            isBuildTime: variable.isBuildTime,
                                            description:
                                              variable.description || "",
                                          });
                                          setEditModalOpen(true);
                                        }}
                                      >
                                        Edit Variable
                                      </Menu.Item>
                                      <Menu.Item
                                        icon={<IconTrash size={14} />}
                                        color="red"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedVariable(variable);
                                          setDeleteModalOpen(true);
                                        }}
                                      >
                                        Delete Variable
                                      </Menu.Item>
                                    </Menu.Dropdown>
                                  </Menu>
                                )}
                              </Group>
                            </td>
                          </motion.tr>
                        ),
                      )}
                    </AnimatePresence>
                  </tbody>
                </Table>
                </ScrollArea>

                {(filteredVariables?.length ?? 0) > 0 && (
                  <Group position="center" mt="xl">
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </motion.div>
            </Card>

            <Modal
              opened={createModalOpen}
              onClose={() => {
                setCreateModalOpen(false);
                setError("");
                form.reset();
              }}
              title="Add Variable"
            >
              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  mb="md"
                  title="Error"
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={form.onSubmit(handleCreateVariable)}>
                <Stack>
                  <TextInput
                    label="Key"
                    placeholder="DATABASE_URL"
                    description="Must be uppercase with underscores"
                    required
                    {...form.getInputProps("key")}
                  />
                  <TextInput
                    label="Value"
                    placeholder="postgres://user:pass@localhost:5432/db"
                    required
                    {...form.getInputProps("value")}
                  />
                  <Switch
                    label="Is Secret"
                    description="Secret values will be masked by default"
                    {...form.getInputProps("isSecret", { type: "checkbox" })}
                  />
                  <Switch
                    label="Available at Build Time"
                    description="This variable will be passed to Cloud Build as a build argument"
                    {...form.getInputProps("isBuildTime", { type: "checkbox" })}
                  />
                  <Textarea
                    label="Description"
                    placeholder="Brief description of this variable"
                    {...form.getInputProps("description")}
                  />
                  <Button
                    type="submit"
                    loading={createVariable.isLoading}
                    fullWidth
                  >
                    Add Variable
                  </Button>
                </Stack>
              </form>
            </Modal>

            <Modal
              opened={editModalOpen}
              onClose={() => {
                setEditModalOpen(false);
                setError("");
                form.reset();
              }}
              title="Edit Variable"
            >
              {error && (
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  color="red"
                  mb="md"
                  title="Error"
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={form.onSubmit(handleUpdateVariable)}>
                <Stack>
                  <TextInput
                    label="Key"
                    value={selectedVariable?.key}
                    disabled
                  />
                  <TextInput
                    label="Value"
                    placeholder="postgres://user:pass@localhost:5432/db"
                    required
                    {...form.getInputProps("value")}
                  />
                  <Switch
                    label="Is Secret"
                    description="Secret values will be masked by default"
                    {...form.getInputProps("isSecret", { type: "checkbox" })}
                  />
                  <Switch
                    label="Available at Build Time"
                    description="This variable will be passed to Cloud Build as a build argument"
                    {...form.getInputProps("isBuildTime", { type: "checkbox" })}
                  />
                  <Textarea
                    label="Description"
                    placeholder="Brief description of this variable"
                    {...form.getInputProps("description")}
                  />
                  <Button
                    type="submit"
                    loading={updateVariable.isLoading}
                    fullWidth
                  >
                    Update Variable
                  </Button>
                </Stack>
              </form>
            </Modal>

            <ConfirmationModal
              opened={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              onConfirm={handleDeleteVariable}
              title="Delete Variable"
              message={`Are you sure you want to delete ${selectedVariable?.key}? This action cannot be undone.`}
              confirmLabel="Delete Variable"
              isLoading={deleteVariable.isLoading}
            />

            <Modal
              opened={historyModalOpen}
              onClose={() => setHistoryModalOpen(false)}
              title={`History for ${selectedVariable?.key}`}
              size="lg"
            >
              <ScrollArea>
              <Table>
                <thead>
                  <tr>
                    <th>Value</th>
                    <th>Changed By</th>
                    <th>Changed At</th>
                  </tr>
                </thead>
                <tbody>
                  {variableHistory?.map((history) => (
                    <tr key={history.id}>
                      <td>
                        {history.isSecret ? (
                          <Group spacing="xs">
                            <Text>••••••••••••</Text>
                            <Tooltip label="Secret values cannot be viewed in history">
                              <ActionIcon size="sm" disabled>
                                <IconEyeOff size={14} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        ) : (
                          <Text>{history.value}</Text>
                        )}
                      </td>
                      <td>
                        <Text size="sm">
                          {history.changedBy.firstName}{" "}
                          {history.changedBy.lastName}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {history.changedBy.email}
                        </Text>
                      </td>
                      <td>
                        <Text size="sm">
                          {new Date(history.changedAt).toLocaleString()}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </ScrollArea>
            </Modal>

            {/* Redeploy Service Confirmation Modal */}
            <Modal
              opened={redeployConfirmOpen}
              onClose={() => {
                setRedeployConfirmOpen(false);
                setDoRebuild(false);
              }}
              title="Redeploy Service"
              withCloseButton={false}
            >
              <Stack>
                <Text>
                  {isEnvironmentDisabled()
                    ? `Are you sure you want to redeploy the service to the "${environment?.name}" environment? This environment is currently disabled, so the deployment will be cancelled and existing deployments will be deleted.`
                    : `Are you sure you want to redeploy the service to the "${environment?.name}" environment?`}
                </Text>
                {!isEnvironmentDisabled() && (
                  <Switch
                    label="Skip build"
                    checked={!doRebuild}
                    onChange={(event) =>
                      setDoRebuild(!event.currentTarget.checked)
                    }
                  />
                )}
                <Group position="apart" mt="md">
                  <Button
                    variant="default"
                    onClick={() => {
                      setRedeployConfirmOpen(false);
                      setDoRebuild(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmRedeployService}
                    loading={triggerDeployment.isLoading}
                    color={isEnvironmentDisabled() ? "orange" : "thanos"}
                  >
                    {isEnvironmentDisabled()
                      ? "Redeploy (Will Cancel)"
                      : "Redeploy Service"}
                  </Button>
                </Group>
              </Stack>
            </Modal>
          </>
        )}
      </PageTransition>
    </Fragment>
  );
}
